import { DimensionSchemaConfig } from '../types/dimension.types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Edit, Trash2, Eye, Lock, GripVertical } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { getCategoryColor, getCategoryIcon } from './dimension-utils';

interface DimensionListProps {
  dimensions: DimensionSchemaConfig[];
  onEdit: (dimension: DimensionSchemaConfig) => void;
  onDelete: (dimensionId: string) => void;
  onPreview: (dimension: DimensionSchemaConfig) => void;
}

export function DimensionList({ dimensions, onEdit, onDelete, onPreview }: DimensionListProps) {
  if (dimensions.length === 0) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-16">
          <div className="text-center">
            <p className="text-muted-foreground">No dimensions found</p>
            <p className="text-sm text-muted-foreground mt-2">
              Create your first dimension to get started
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {dimensions.map((dimension) => (
        <DimensionCard
          key={dimension.id}
          dimension={dimension}
          onEdit={() => onEdit(dimension)}
          onDelete={() => onDelete(dimension.id)}
          onPreview={() => onPreview(dimension)}
        />
      ))}
    </div>
  );
}

interface DimensionCardProps {
  dimension: DimensionSchemaConfig;
  onEdit: () => void;
  onDelete: () => void;
  onPreview: () => void;
}

function DimensionCard({ dimension, onEdit, onDelete, onPreview }: DimensionCardProps) {
  const categoryColor = getCategoryColor(dimension.category);
  const categoryIcon = getCategoryIcon(dimension.category);

  const requiredFieldsCount = dimension.fields.filter(f => f.required).length;
  const indexedFieldsCount = dimension.fields.filter(f => f.indexed).length;

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            {categoryIcon}
            <div>
              <CardTitle className="text-lg">{dimension.name}</CardTitle>
              <CardDescription className="text-xs mt-1">
                {dimension.tableName || `dim_${dimension.id}`}
              </CardDescription>
            </div>
          </div>
          {dimension.isSystem && (
            <Badge variant="secondary" className="gap-1">
              <Lock className="h-3 w-3" />
              System
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Description */}
        {dimension.description && (
          <p className="text-sm text-muted-foreground line-clamp-2">
            {dimension.description}
          </p>
        )}

        {/* Stats */}
        <div className="flex items-center gap-3 text-sm">
          <div className="flex items-center gap-1">
            <GripVertical className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">{dimension.fields.length} fields</span>
          </div>
          {requiredFieldsCount > 0 && (
            <Badge variant="outline" className="text-xs">
              {requiredFieldsCount} required
            </Badge>
          )}
          {indexedFieldsCount > 0 && (
            <Badge variant="outline" className="text-xs">
              {indexedFieldsCount} indexed
            </Badge>
          )}
        </div>

        {/* Category Badge */}
        <div>
          <Badge
            variant="secondary"
            className={`${categoryColor} text-white`}
          >
            {dimension.category}
          </Badge>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <Button variant="outline" size="sm" className="flex-1" onClick={onPreview}>
            <Eye className="h-4 w-4 mr-2" />
            Preview
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                •••
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onPreview}>
                <Eye className="h-4 w-4 mr-2" />
                View Details
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onEdit}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </DropdownMenuItem>
              {!dimension.isSystem && (
                <DropdownMenuItem onClick={onDelete} className="text-destructive">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardContent>
    </Card>
  );
}
